// Global Instructions Rule Applied!

import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Checkbox, Modal, Select, Space, Typography } from 'antd'
import type { FundStatusAdminRow } from '../../../state_manager/data_models/fund_statuses_config_model.ts'

const { Text } = Typography

interface ReplaceFundStatusModalProps {
  open: boolean
  statuses: FundStatusAdminRow[]
  initialSourceIds?: string[]
  confirmLoading?: boolean
  onConfirm: (sourceIds: string[], targetId: string, deleteSources: boolean) => void
  onCancel: () => void
}

// Replace (or merge) one-or-more fund statuses into a single target:
// every reference — stage library, comms LP rules, follower-agent exclusion
// criteria, the app_config status lists — is repointed atomically. This is
// also the forced flow when deleting an in-use status. Sources and target
// must share a category (the backend refuses cross-category replacement), so
// the pickers are constrained to the first chosen source's category.
export function ReplaceFundStatusModal({
  open,
  statuses,
  initialSourceIds,
  confirmLoading,
  onConfirm,
  onCancel
}: ReplaceFundStatusModalProps) {
  const [sourceIds, setSourceIds] = useState<string[]>([])
  const [targetId, setTargetId] = useState<string | undefined>(undefined)
  const [deleteSources, setDeleteSources] = useState(true)

  // Reset the form whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setSourceIds(initialSourceIds ?? [])
      setTargetId(undefined)
      setDeleteSources(true)
    }
  }, [open, initialSourceIds])

  const category = useMemo(() => {
    const first = statuses.find((s) => sourceIds.includes(s.fund_statuses__id))
    return first?.fund_statuses__category
  }, [statuses, sourceIds])

  const sourceOptions = useMemo(
    () =>
      statuses
        .filter((s) => !category || s.fund_statuses__category === category)
        .map((s) => ({ value: s.fund_statuses__id, label: s.fund_statuses__name })),
    [statuses, category]
  )

  // The target cannot be a source, and must share the sources' category.
  const targetOptions = useMemo(
    () =>
      statuses
        .filter(
          (s) =>
            !sourceIds.includes(s.fund_statuses__id) &&
            (!category || s.fund_statuses__category === category)
        )
        .map((s) => ({ value: s.fund_statuses__id, label: s.fund_statuses__name })),
    [statuses, sourceIds, category]
  )

  const affectedReferences = useMemo(
    () =>
      statuses
        .filter((s) => sourceIds.includes(s.fund_statuses__id))
        .reduce((sum, s) => sum + (s.fund_statuses__usage_count || 0), 0),
    [statuses, sourceIds]
  )

  const targetName = statuses.find(
    (s) => s.fund_statuses__id === targetId
  )?.fund_statuses__name

  const canConfirm = sourceIds.length > 0 && !!targetId && !sourceIds.includes(targetId)

  return (
    <Modal
      title='Replace status'
      open={open}
      okText='Reassign'
      okButtonProps={{ disabled: !canConfirm }}
      confirmLoading={confirmLoading}
      onOk={() => {
        if (canConfirm && targetId) onConfirm(sourceIds, targetId, deleteSources)
      }}
      onCancel={onCancel}
      destroyOnClose
    >
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <div>
          <Text strong>Statuses to replace</Text>
          <Select
            mode='multiple'
            allowClear
            placeholder='Select one or more statuses'
            value={sourceIds}
            options={sourceOptions}
            onChange={(vals) => {
              setSourceIds(vals)
              if (targetId && vals.includes(targetId)) setTargetId(undefined)
            }}
            optionFilterProp='label'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <div>
          <Text strong>Replace with</Text>
          <Select
            allowClear
            showSearch
            placeholder='Select the target status (same pipeline kind)'
            value={targetId}
            options={targetOptions}
            onChange={(val) => setTargetId(val)}
            optionFilterProp='label'
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <Checkbox checked={deleteSources} onChange={(e) => setDeleteSources(e.target.checked)}>
          Delete the replaced {sourceIds.length === 1 ? 'status' : 'statuses'} after reassigning
        </Checkbox>

        {canConfirm && (
          <Alert
            type='info'
            showIcon
            message={`${affectedReferences} reference(s) — stages, LP status rules and agent criteria — will move to “${targetName}”.`}
          />
        )}
      </Space>
    </Modal>
  )
}
